import { Controller } from "@hotwired/stimulus";
import { CsrfToken } from "../shared/csrf_token";

export default class extends Controller {
  static values = { currentView: String, saveUrl: String };

  connect() {
    CsrfToken(this);
    this.setupAutoSave();
  }

  setupAutoSave() {
    // フォーム要素を取得
    this.form =
      this.element.closest("form") || this.element.querySelector("form");
    if (!this.form) {
      console.warn("Form not found for auto-save");
      return;
    }

    // 前回の値を保存するためのオブジェクト
    this.previousValues = {};

    // デバウンス用のタイマー
    this.debounceTimer = null;

    // 同時送信を避けるための状態
    this.saving = false;
    this.pendingSave = false;

    // 初期値を記録
    this.recordInitialValues();

    // イベントリスナーを設定
    this.setupEventListeners();
  }

  recordInitialValues() {
    // すべてのフォーム要素の初期値を記録
    const formData = new FormData(this.form);
    for (const [key, value] of formData.entries()) {
      this.previousValues[key] = value;
    }
  }

  setupEventListeners() {
    // バインドされた関数を保持
    this.boundHandleChange = this.handleChange.bind(this);
    this.boundHandleInput = this.handleInput.bind(this);
    this.boundHandleClick = this.handleClick.bind(this);

    // changeイベント（フォーカスが外れた時）
    this.form.addEventListener("change", this.boundHandleChange);

    // inputイベント（入力中）- デバウンス付き
    this.form.addEventListener("input", this.boundHandleInput);

    // 削除ボタンクリックなど change/input を伴わないフォーム変更を捕捉する。
    this.form.addEventListener("click", this.boundHandleClick);
  }

  handleChange(event) {
    // 即座に保存を試行
    this.attemptSave();
  }

  handleInput(event) {
    // デバウンス処理（3秒後に保存）
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.attemptSave();
    }, 3000);
  }

  handleClick(event) {
    const trigger = event.target.closest("[data-action]");
    if (!trigger) return;

    const action = trigger.dataset.action || "";

    if (action.includes("resumin--form-removal#remove")) {
      this.attemptSave();
      return;
    }

    if (
      action.includes("resumin--sortable-list#moveUp") ||
      action.includes("resumin--sortable-list#moveDown")
    ) {
      setTimeout(() => this.attemptSave(), 400);
      return;
    }
  }

  attemptSave() {
    // 現在の値を取得
    const currentValues = this.snapshotFormValues();

    // 変更があるかチェック
    if (this.hasValueChanges(currentValues)) {
      this.saveForm();
    }
  }

  snapshotFormValues() {
    const values = {};
    const formData = new FormData(this.form);
    for (const [key, value] of formData.entries()) {
      values[key] = value;
    }
    return values;
  }

  hasValueChanges(currentValues) {
    // 前回の値と比較
    for (const [key, value] of Object.entries(currentValues)) {
      if (this.previousValues[key] !== value) {
        return true;
      }
    }

    // 削除されたフィールドもチェック
    for (const key of Object.keys(this.previousValues)) {
      if (!(key in currentValues)) {
        return true;
      }
    }

    return false;
  }

  async saveForm() {
    if (this.saving) {
      this.pendingSave = true;
      return;
    }

    this.saving = true;
    try {
      const formData = new FormData(this.form);

      // 実際にサーバへ送る値のスナップショット。応答後に previousValues に反映する。
      const sentValues = {};
      for (const [key, value] of formData.entries()) {
        sentValues[key] = value;
      }

      // 現在のビューとネクストビューを設定（同じページに留まる）
      formData.set("current_view", this.currentViewValue);
      formData.set("next_view", this.currentViewValue);

      // オートセーブフラグを追加
      formData.set("auto_save", "true");

      const response = await fetch(this.saveUrlValue, {
        method: "PUT",
        body: formData,
        headers: {
          ...this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        console.error(
          "Auto-save failed:",
          response.status,
          response.statusText,
        );
        return;
      }

      const data = await this.parseJson(response);
      if (data && data.nested_ids) {
        this.applyNestedIds(data.nested_ids, sentValues);
      }

      this.pruneProcessedDestroyGroups(sentValues);

      Object.assign(this.previousValues, sentValues);
    } catch (error) {
      console.error("Auto-save error:", error);
    } finally {
      this.saving = false;
      if (this.pendingSave) {
        this.pendingSave = false;
        this.attemptSave();
      }
    }
  }

  async parseJson(response) {
    try {
      return await response.json();
    } catch (_e) {
      return null;
    }
  }

  pruneProcessedDestroyGroups(sentValues) {
    const destroyInputs = this.form.querySelectorAll(
      'input[type="hidden"][name$="[_destroy]"]',
    );
    destroyInputs.forEach((destroyInput) => {
      const value = destroyInput.value;
      if (value !== "true" && value !== "1") return;

      const groupPrefix = destroyInput.name.slice(0, -"[_destroy]".length);
      const groupInputs = this.form.querySelectorAll(
        `[name^="${groupPrefix}["]`,
      );
      groupInputs.forEach((input) => {
        delete this.previousValues[input.name];
        delete sentValues[input.name];
        input.remove();
      });
    });
  }

  applyNestedIds(nestedIds, sentValues) {
    Object.entries(nestedIds).forEach(([attrsKey, indexMap]) => {
      Object.entries(indexMap).forEach(([clientIndex, recordId]) => {
        const nameSuffix = `[${attrsKey}][${clientIndex}][id]`;
        const input = this.form.querySelector(
          `input[type="hidden"][name$="${nameSuffix}"]`,
        );
        if (!input) return;
        const stamped = String(recordId);
        input.value = stamped;
        sentValues[input.name] = stamped;
      });
    });
  }

  disconnect() {
    // タイマーをクリア
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // イベントリスナーを削除
    if (this.form) {
      this.form.removeEventListener("change", this.boundHandleChange);
      this.form.removeEventListener("input", this.boundHandleInput);
      this.form.removeEventListener("click", this.boundHandleClick);
    }
  }
}
